import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { ShieldOff } from 'lucide-react';

const ADMIN_ROLES = ['superadmin', 'manager', 'analyst'];

interface AdminRouteProps {
  children: React.ReactNode;
  requiredRole?: 'superadmin' | 'manager' | 'analyst' | 'any';
}

export default function AdminRoute({ children, requiredRole = 'any' }: AdminRouteProps) {
  const { user, loading } = useAuth();
  const { t } = useLanguage();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="w-8 h-8 border-4 border-goszakup-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const isLoggedIn = !!(
    localStorage.getItem('access_token') || sessionStorage.getItem('access_token')
  );

  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="w-8 h-8 border-4 border-goszakup-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const hasAccess =
    requiredRole === 'any'
      ? ADMIN_ROLES.includes(user.role)
      : requiredRole === 'superadmin'
      ? user.role === 'superadmin'
      : user.role === 'superadmin' || user.role === requiredRole;

  if (!hasAccess) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-slate-50 gap-4 p-6 text-center">
        <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center">
          <ShieldOff className="w-8 h-8 text-red-500" />
        </div>
        <h2 className="text-xl font-extrabold text-gray-900">{t('admin.accessDenied')}</h2>
        <p className="text-gray-500 max-w-sm">{t('admin.accessDeniedDesc')}</p>
        <a
          href="/"
          className="mt-2 px-6 py-2.5 rounded-xl bg-goszakup-600 text-white font-bold text-sm hover:bg-goszakup-700 transition-colors"
        >
          {t('admin.backToSite')}
        </a>
      </div>
    );
  }

  return <>{children}</>;
}
